import { Link } from 'react-router-dom';
import type { SidebarProject } from '@/hooks/useSidebarProjects';

interface Props {
  projects: SidebarProject[];
  loading?: boolean;
  hasResolved?: boolean;
  error?: string | null;
  onRetry?: () => void;
  currentProjectId?: string;
  buildProjectUrl: (projectId: string) => string;
  emptyLabel?: string;
  onProjectClick?: () => void;
}

function SidebarProjectSkeleton() {
  return (
    <div className="ml-2 pl-3 border-l border-white/10 py-1 space-y-1.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-6 mx-2 rounded-lg bg-white/5 animate-pulse" />
      ))}
    </div>
  );
}

export default function SidebarProjectList({
  projects,
  loading = false,
  hasResolved = false,
  error = null,
  onRetry,
  currentProjectId,
  buildProjectUrl,
  emptyLabel = 'No projects',
  onProjectClick,
}: Props) {
  if (loading || !hasResolved) {
    return <SidebarProjectSkeleton />;
  }

  if (error) {
    return (
      <div className="ml-2 pl-3 border-l border-white/10 py-1">
        <p className="text-white/45 text-[10px] px-2 py-1 leading-snug">{error}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mx-2 mt-0.5 px-2 py-1 rounded-md text-[10px] font-semibold text-[#E8621A] hover:bg-white/5 cursor-pointer"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="ml-2 pl-3 border-l border-white/10 py-1">
        <p className="text-white/30 text-[10px] px-2 py-1">{emptyLabel}</p>
      </div>
    );
  }

  return (
    <div className="ml-2 pl-3 border-l border-white/10 mt-1 mb-2 max-h-[min(50vh,360px)] overflow-y-auto scrollbar-thin">
      {projects.map((project) => {
        const isActive = currentProjectId === project.id;
        return (
          <Link
            key={project.id}
            to={buildProjectUrl(project.id)}
            title={project.project_name}
            onClick={onProjectClick}
            className={`block px-2 py-1.5 rounded-lg text-[11px] truncate transition-colors ${
              isActive
                ? 'bg-[#E8621A]/20 text-white font-semibold'
                : 'text-white/45 hover:text-white hover:bg-white/5'
            }`}
          >
            {project.project_name}
          </Link>
        );
      })}
    </div>
  );
}
